import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { Badge, BadgeFormaPago, estadoVisible, sumarPorMoneda, textoPorMoneda, mapaEstadosSolicitud } from "../utils/estados";
import { Ayuda } from "../context/AyudaContext";
import Cargando from "../components/Cargando";

// Lista resumida de lo que está esperando aprobación: una fila por solicitud.
// Todo el detalle (productos, cotizaciones, aprobar/rechazar) vive en la
// pantalla de detalle de cada solicitud (/pendientes/:id).
function SolicitudesPendientes() {
  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");
  const primerNombre = usuario?.nombre?.split(" ")[0] || "";
  const hora = new Date().getHours();
  const saludoHora = hora < 12 ? "Buenos días" : hora < 19 ? "Buenas tardes" : "Buenas noches";

  const navigate = useNavigate();
  // Cuántas solicitudes ya aprobó este usuario (para la tarjeta "Mis aprobaciones").
  const [misAprobadas, setMisAprobadas] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  const cargar = async () => {
    setCargando(true);
    setError("");
    try {
      const resultado = await apiFetch("/api/solicitudes?estado=pendiente_aprobacion");
      const detalles = await Promise.all(
        (resultado?.items || []).map((s) => apiFetch(`/api/solicitudes/${s.id}`))
      );
      setSolicitudes(detalles);
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    apiFetch("/api/solicitudes/aprobadas-por-mi")
      .then((data) => setMisAprobadas(data?.items || []))
      .catch(() => setMisAprobadas([]));
  }, []);

  if (cargando) return <Cargando />;

  // Solo cuentan los productos que aún están por decidir; lo ya aprobado o rechazado no se vuelve a ofrecer.
  const idsPendientesDe = (s) =>
    new Set(
      s.items
        .filter(
          (it) =>
            it.estado_item === "validado" &&
            !it.aprobado &&
            !it.descartado &&
            Number(it.cantidad) - Number(it.cantidad_aprobada || 0) - Number(it.cantidad_descartada || 0) > 0
        )
        .map((it) => it.id)
    );
  // Cotizaciones elegidas que cubren algo pendiente: los demás proveedores (ya resueltos) no se muestran.
  const elegidas = (s) => {
    const pendientes = idsPendientesDe(s);
    const conPendiente = (c) => c.items.some((ci) => pendientes.has(ci.id) && !ci.aprobada);
    const recomendadas = s.cotizaciones.filter((c) => c.seleccionada && conPendiente(c));
    // Si lo que falta (ej. el monitor que quedó sin aprobar) solo lo cubre una cotización que compras no marcó
    // como recomendada, la solicitud igual se muestra: si no, desaparecería de la lista estando pendiente.
    return recomendadas.length > 0 ? recomendadas : s.cotizaciones.filter(conPendiente);
  };
  // Monto de una cotización limitado a sus productos pendientes (con IVA, salvo proveedor exonerado).
  const montoPendienteDe = (s, c) => {
    const pendientes = idsPendientesDe(s);
    const lineas = c.items.filter((ci) => pendientes.has(ci.id) && !ci.aprobada);
    if (lineas.length === c.items.length) return Number(c.monto_total);
    const subtotal = lineas.reduce((suma, ci) => suma + Number(ci.precio || 0), 0);
    return c.exento_iva ? subtotal : Math.round(subtotal * (1 + Number(c.iva_porcentaje ?? 15) / 100) * 100) / 100;
  };
  // Cada monto va en la moneda de su cotización; si hay de las dos, se muestra uno por moneda.
  const montosRecomendados = (s) =>
    sumarPorMoneda(mias(s).map((c) => ({ moneda: c.moneda, monto: montoPendienteDe(s, c) })));
  // De esas, solo las que ESTE aprobador puede aprobar por su rango de montos: los proveedores y montos que se
  // muestran en la lista son los suyos (antes se sumaba también lo que le corresponde a otra persona).
  const mias = (s) => elegidas(s).filter((c) => c.puede_aprobar !== false);
  const unicos = (lista) => [...new Set(lista.filter(Boolean))];

  // Un aprobador con rango de montos solo ve lo que le corresponde por el
  // monto de la cotización elegida; lo demás lo aprueba otra persona.
  const meCorresponde = (s) => elegidas(s).some((c) => c.puede_aprobar !== false);
  const solicitudesVisibles = solicitudes.filter(meCorresponde);
  const ocultasPorMonto = solicitudes.length - solicitudesVisibles.length;
  const rangoAprobacion = solicitudes.find((s) => s.rango_aprobacion)?.rango_aprobacion;

  // Las urgentes van primero — el aprobador no se las puede quedar mirando
  // más abajo en la lista.
  const esUrgente = (s) => (s.items || []).some((it) => it.prioridad === "urgente");
  const ordenadas = [...solicitudesVisibles].sort((a, b) => Number(esUrgente(b)) - Number(esUrgente(a)));

  return (
    <div>
      <h2 style={{ margin: 0, color: "var(--tema-titulo, #0b3c60)", textAlign: "center" }}>✅ Aprobaciones</h2>
      <Ayuda>
        <p style={{ color: "#5b6472", marginTop: "0.25rem", fontSize: "0.9rem", textAlign: "center" }}>
          Elige una solicitud y toca "Ver detalle" para revisarla, aprobarla o rechazarla.
        </p>
      </Ayuda>

      <div className="tarjetas-resumen" style={styles.tarjetas}>
        <div style={{ ...styles.tarjeta, cursor: "default" }}>
          <span style={{ ...styles.tarjetaIcono, color: "#b85a0b", background: "#fde6cf" }}>⏳</span>
          <span style={{ textAlign: "left" }}>
            <span style={styles.tarjetaTitulo}>Pendientes de aprobar</span>
            <span style={styles.tarjetaValor}>{ordenadas.length}</span>
            <span style={styles.tarjetaMonto}>
              {textoPorMoneda(sumarPorMoneda(ordenadas.flatMap((s) => mias(s).map((c) => ({ moneda: c.moneda, monto: montoPendienteDe(s, c) })))))}
              {ordenadas.some(esUrgente) && (
                <span style={{ color: "#a3402f", fontWeight: 700 }}>
                  {" "}
                  · 🔴 {ordenadas.filter(esUrgente).length} urgente{ordenadas.filter(esUrgente).length === 1 ? "" : "s"}
                </span>
              )}
            </span>
          </span>
        </div>
        <button type="button" onClick={() => navigate("/aprobadas")} style={styles.tarjeta} title="Ver lo que ya aprobaste">
          <span style={{ ...styles.tarjetaIcono, color: "#2f7a54", background: "#dcf0e6" }}>🗂️</span>
          <span style={{ textAlign: "left" }}>
            <span style={styles.tarjetaTitulo}>Mis aprobaciones</span>
            <span style={styles.tarjetaValor}>{misAprobadas ? misAprobadas.length : "—"}</span>
            <span style={styles.tarjetaMonto}>Ver lo que ya aprobé →</span>
          </span>
        </button>
      </div>

      {error && <p style={{ color: "#a3402f", marginTop: "1rem" }}>{error}</p>}

      {(rangoAprobacion || ocultasPorMonto > 0) && (
        <div style={styles.avisoRango}>
          {rangoAprobacion && (
            <>
              💼 Tu rango de aprobación: <strong>{rangoAprobacion}</strong>.{" "}
            </>
          )}
          {ocultasPorMonto > 0 &&
            `Hay ${ocultasPorMonto} solicitud(es) pendiente(s) cuyo monto le corresponde aprobar a otra persona.`}
        </div>
      )}

      {!error && solicitudesVisibles.length === 0 && (
        <div className="vacio-card" style={styles.vacioCard}>
          <div style={styles.vacioIconoWrap}>
            <div style={styles.vacioGlow} />
            <span className="vacio-anillo" style={styles.vacioAnillo} />
            <div style={styles.vacioIcono}>✅</div>
          </div>
          <p style={styles.vacioEyebrow}>
            {saludoHora}
            {primerNombre ? `, ${primerNombre}` : ""} 👋
          </p>
          <h3 style={styles.vacioTitulo}>¡Todo al día!</h3>
          <p style={styles.vacioTexto}>
            No hay solicitudes esperando tu aprobación por ahora. En cuanto compras suba una
            cotización, aparecerá aquí.
          </p>
          <button type="button" className="vacio-refrescar" onClick={cargar} style={styles.vacioBoton}>
            🔄 Actualizar
          </button>
        </div>
      )}

      {ordenadas.length > 0 && (
        <div style={styles.tarjetaTabla}>
          <div className="table-scroll">
            <table style={styles.tabla}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid #d3d9df" }}>
                  <th style={styles.th}>Solicitud</th>
                  <th style={styles.th}>Solicitante</th>
                  <th style={styles.th}>Área</th>
                  <th style={styles.th}>Requerimiento</th>
                  <th style={styles.th}>Proveedor recomendado</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Monto recomendado</th>
                  <th style={styles.th}>Forma de pago</th>
                  <th style={styles.th}>Estado</th>
                  <th style={styles.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {ordenadas.map((s, i) => (
                  <tr
                    key={s.id}
                    style={{
                      borderBottom: "1px solid #eef1f3",
                      background: i % 2 === 1 ? "#fafbfc" : "#fff",
                      borderLeft: esUrgente(s) ? "4px solid #a3402f" : "4px solid transparent",
                    }}
                  >
                    <td style={{ ...styles.td, fontWeight: 700, color: "var(--tema-titulo, #0b3c60)", whiteSpace: "nowrap" }}>
                      #{String(s.id).padStart(3, "0")}
                      {esUrgente(s) && <div style={styles.badgeUrgente}>🔴 Urgente</div>}
                    </td>
                    <td style={styles.td}>{s.solicitante_nombre}</td>
                    <td style={styles.td}>{s.area_nombre}</td>
                    <td style={{ ...styles.td, maxWidth: 280, lineHeight: 1.35 }}>{s.descripcion_requerimiento}</td>
                    <td style={styles.td}>
                      {unicos(mias(s).map((c) => c.proveedor_nombre)).map((nombre) => (
                        <div key={nombre}>{nombre}</div>
                      ))}
                      <div style={styles.recomendada}>★ Recomendada por compras</div>
                    </td>
                    <td style={{ ...styles.td, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                      {textoPorMoneda(montosRecomendados(s))}
                    </td>
                    <td style={styles.td}>
                      {/* Sin forma de pago definida también se muestra ("Por definir"), no una celda vacía. */}
                      {[...new Set(mias(s).map((c) => c.forma_pago || ""))].map((forma) => (
                        <div key={forma || "por-definir"} style={{ marginBottom: "0.2rem" }}>
                          <BadgeFormaPago valor={forma || null} />
                        </div>
                      ))}
                    </td>
                    <td style={styles.td}>
                      <Badge
                        estado={estadoVisible(
                          s.estado,
                          s.total_cotizaciones,
                          s.tiene_pendiente_aprobacion,
                          s.en_atencion,
                          s.tiene_pago_pendiente,
                          s.tiene_pago_realizado
                        )}
                        mapa={mapaEstadosSolicitud()}
                      />
                    </td>
                    <td style={styles.td}>
                      <Link to={`/pendientes/${s.id}`} style={styles.botonDetalle}>
                        👁 Ver detalle
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  tarjetas: { display: "flex", flexWrap: "wrap", gap: "0.8rem", marginTop: "1.1rem" },
  tarjeta: {
    flex: "1 1 260px",
    display: "flex",
    alignItems: "center",
    gap: "0.8rem",
    border: "1px solid #e2e6ea",
    borderRadius: 12,
    padding: "0.85rem 1rem",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  tarjetaIcono: {
    width: 42,
    height: 42,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.2rem",
    flexShrink: 0,
  },
  tarjetaTitulo: { display: "block", fontSize: "0.74rem", fontWeight: 700, color: "#5b6472", textTransform: "uppercase", letterSpacing: "0.03em" },
  tarjetaValor: { display: "block", fontSize: "1.5rem", fontWeight: 800, color: "var(--tema-titulo, #0b3c60)", lineHeight: 1.15, fontVariantNumeric: "tabular-nums" },
  tarjetaMonto: { display: "block", fontSize: "0.76rem", color: "#8993a1", fontVariantNumeric: "tabular-nums" },
  avisoRango: {
    marginTop: "1rem",
    padding: "0.6rem 0.9rem",
    borderRadius: 8,
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    fontSize: "0.85rem",
  },
  vacioCard: {
    marginTop: "1.5rem",
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    padding: "3rem 1.5rem 2.5rem",
    background: "linear-gradient(180deg, #f4f9f6 0%, #fff 55%)",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
    textAlign: "center",
  },
  vacioIconoWrap: { position: "relative", width: 96, height: 96, margin: "0 auto 0.9rem" },
  vacioGlow: {
    position: "absolute",
    inset: -10,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(47,122,84,0.16), rgba(47,122,84,0) 70%)",
  },
  vacioAnillo: { position: "absolute", inset: 16, borderRadius: "50%", border: "2px solid #2f7a54" },
  vacioIcono: {
    position: "absolute",
    inset: 16,
    borderRadius: "50%",
    background: "#dcf0e6",
    color: "#2f7a54",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.8rem",
  },
  vacioEyebrow: { margin: "0 0 0.25rem", color: "#2f7a54", fontSize: "0.82rem", fontWeight: 700 },
  vacioTitulo: { margin: "0 0 0.4rem", color: "var(--tema-titulo, #0b3c60)", fontSize: "1.25rem" },
  vacioTexto: { margin: "0 auto", maxWidth: 380, color: "#5b6472", fontSize: "0.88rem", lineHeight: 1.5 },
  vacioBoton: {
    marginTop: "1.25rem",
    background: "#fff",
    border: "1px solid #d3d9df",
    borderRadius: 8,
    padding: "0.5rem 1.1rem",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "var(--tema-titulo, #0b3c60)",
    cursor: "pointer",
  },
  tarjetaTabla: {
    marginTop: "1.2rem",
    border: "1px solid #e2e6ea",
    borderRadius: 14,
    background: "#fff",
    boxShadow: "0 1px 3px rgba(27,42,74,0.06)",
    overflow: "hidden",
  },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.86rem" },
  th: {
    padding: "0.6rem 0.7rem",
    background: "#eef1f3",
    fontSize: "0.75rem",
    color: "var(--tema-titulo, #0b3c60)",
    whiteSpace: "nowrap",
  },
  td: { padding: "0.7rem", verticalAlign: "top", color: "var(--tema-titulo, #0b3c60)" },
  recomendada: { marginTop: "0.2rem", fontSize: "0.7rem", fontWeight: 700, color: "#9c7a1a", whiteSpace: "nowrap" },
  badgeUrgente: {
    display: "inline-block",
    marginTop: "0.25rem",
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#a3402f",
    background: "#f7ded9",
    borderRadius: 999,
    padding: "0.1rem 0.55rem",
    whiteSpace: "nowrap",
  },
  botonDetalle: {
    display: "inline-block",
    background: "var(--tema-titulo, #0b3c60)",
    color: "#fff",
    borderRadius: 8,
    padding: "0.4rem 0.8rem",
    fontSize: "0.8rem",
    fontWeight: 700,
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
};

export default SolicitudesPendientes;
